import React, { useState, useEffect, useRef } from 'react';
import {
  Calendar,
  message,
  Modal,
  Input,
  Badge,
  DatePicker,
  Select,
  Switch,
  Table,
  Space,
  Popconfirm,
  Button,
  Divider,
} from 'antd';
import type { InputRef } from 'antd/es/input'; // or "antd/lib/input" depending on your setup
import {
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CalendarOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import api from '../../services/api';
import { useGlobalContext } from '../../store/GlobalContext';

const { Option } = Select;

// Interfaces for your data
interface IEvent {
  id: string;
  estateId: string;
  title: string;
  start: string;
  end?: string;
  location?: string;
  description?: string;
  eventAttendees?: string;
  templateMode?: boolean;
  templateGroup?: string;
  pivotEventOffset?: number | string;
  anchorEvent?: string;
  anchorEventNext?: string;
  iCalendar?: string;
  gCalendar?: string;
  happensAfter?: string;
  happensBefore?: string;
}

interface ITemplate {
  id: string;
  templateName: string;
  applyToAll?: boolean;
  updatedAt?: string;
}

interface ITemplateListTableProps {
  templates: ITemplate[];
  onApplyToAll: (templateName: string) => void;
  createAllEventsOnTemplate: (templateName: string, start: string) => void;
  isAdmin: boolean;
}

interface IEventsTableProps {
  events: IEvent[];
  onDelete: (id: string) => void;
  onEdit: (evt: IEvent) => void;
}

interface ICalendarComponentProps {
  isAdmin?: boolean;
}

/** TemplateListTable component */
const TemplateListTable: React.FC<ITemplateListTableProps> = ({
  templates,
  onApplyToAll,
  createAllEventsOnTemplate,
  isAdmin,
}) => {
  const columns = [
    {
      title: 'Template Name',
      dataIndex: 'templateName',
      key: 'templateName',
      render: (text: string) => text,
    },
    {
      title: 'Applies To All Status',
      dataIndex: 'applyToAll',
      key: 'applyToAll',
      render: (applyToAll: boolean) => (applyToAll ? 'Yes' : 'No'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt: string) => moment(updatedAt).fromNow(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: ITemplate) => (
        <Space size="middle">
          {isAdmin && (
            <Popconfirm
              title="Are you sure you want to apply this template?"
              onConfirm={() => onApplyToAll(record.templateName)}
              okText="Yes"
              cancelText="No"
            >
              <a>Apply To All</a>
            </Popconfirm>
          )}
          <Popconfirm
            title="This will recreate all events in this template. If already done, you'll have duplicate events. Continue?"
            onConfirm={() =>
              createAllEventsOnTemplate(record.templateName, record.updatedAt ?? '')
            }
            okText="Yes"
            cancelText="No"
          >
            <a>
              <DownloadOutlined /> Create Events
            </a>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return <Table<ITemplate> columns={columns} rowKey="id" dataSource={templates} />;
};

/** EventsTable component */
const EventsTable: React.FC<IEventsTableProps> = ({ events, onDelete, onEdit }) => {
  const columns = [
    {
      title: 'Event',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Due Date',
      dataIndex: 'start',
      key: 'start',
      render: (start: string) => dayjs(start).format('MMM Do YYYY'),
    },
    {
      title: 'Days remaining',
      dataIndex: 'start',
      key: 'days',
      render: (start: string) => moment(start).fromNow(),
    },
    {
      title: 'Happens after',
      dataIndex: 'happensAfter',
      key: 'happensAfter',
    },
    {
      title: 'Happens before',
      dataIndex: 'happensBefore',
      key: 'happensBefore',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IEvent) => (
        <Space size="middle">
          <a onClick={() => onEdit(record)}>
            <EditOutlined />
          </a>
          <Popconfirm
            title="Are you sure you want to delete this event?"
            onConfirm={() => onDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <a>
              <DeleteOutlined />
            </a>
          </Popconfirm>
          {record.iCalendar && (
            <a href={'data:application/octet-stream,' + record.iCalendar}>
              <DownloadOutlined />
            </a>
          )}
          {record.gCalendar && (
            <a href={record.gCalendar} target="_blank" rel="noopener noreferrer">
              <CalendarOutlined />
            </a>
          )}
        </Space>
      ),
    },
  ];

  return <Table<IEvent> columns={columns} rowKey="id" dataSource={events} />;
};

/** Main CalendarComponent */
const CalendarComponent: React.FC<ICalendarComponentProps> = ({ isAdmin = false }) => {
  const [events, setEvents] = useState<IEvent[]>([]);
  const [templates, setTemplates] = useState<ITemplate[]>([]);

  // Modal controls
  const [eventFormVisible, setEventFormVisible] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);

  // Event fields
  const [currentEvent, setCurrentEvent] = useState<IEvent | null>(null);
  const [eventTitle, setEventTitle] = useState<string>('');
  const [eventStart, setEventStart] = useState<Dayjs | null>(null);
  const [eventEnd, setEventEnd] = useState<Dayjs | null>(null);
  const [eventLocation, setEventLocation] = useState<string>('');
  const [eventDescription, setEventDescription] = useState<string>('');
  const [eventAttendees, setEventAttendees] = useState<string>('');
  const [templateMode, setTemplateMode] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>('');
  const [anchorEvent, setAnchorEvent] = useState<string>('');
  const [anchorEventNext, setAnchorEventNext] = useState<string>('');
  const [offsetType, setOffsetType] = useState<string>('after');
  const [offsetValue, setOffsetValue] = useState<string>('');

  // For inline creation of new template items in the <Select>
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const calendarTemplateInputRef = useRef<InputRef>(null);

  const { selectedEstate } = useGlobalContext();
  const estateId = selectedEstate?.uuid || '';

  /** Fetch all templates */
  const fetchTemplates = async () => {
    // try {
    //   const response = await api.get(
    //     '/calendar/templates/all'
    //   );
    //   if (response) {
    //     setTemplates(response.data.templates);
    //   } else {
    //     message.error('Failed to fetch templates');
    //   }
    // } catch (err) {
    //   console.error(err);
    //   message.error('Failed to fetch templates');
    // }
  };

  /** Fetch all events */
  const fetchEvents = async () => {
    try {
      const response = await api.get('/calendar');
      if (response) {
        setEvents(response.data);
      } else {
        message.error('Failed to fetch events');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch events');
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchEvents();
  }, []);

  /** Renders a list of events on each date cell */
  const handleDateCellRender = (value: Dayjs) => {
    const eventsOnDate = events.filter((event) =>
      dayjs(event.start).isSame(value, 'day')
    );
    return (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {eventsOnDate.map((event) => (
          <li key={event.id}>
            <Badge status="success" text={event.title} />
          </li>
        ))}
      </ul>
    );
  };

  /** Opens modal for creating a new event when clicking a date */
  const handleDateSelect = (value: Dayjs) => {
    setEditing(false);
    setCurrentEvent(null);
    setEventTitle('');
    setEventStart(value);
    setEventEnd(null);
    setEventLocation('');
    setEventDescription('');
    setEventAttendees('');
    setTemplateMode(false);
    setTemplateName('');
    setAnchorEvent('');
    setAnchorEventNext('');
    setOffsetType('after');
    setOffsetValue('');
    setEventFormVisible(true);
  };

  /** "Apply To All" template action */
  const onApplyToAll = async (name: string) => {
    try {
      const response = await api.post('/calendar/applyToAll', {
        data: { templateName: name },
        getResponse: true,
      });
      if (response.status === 200) {
        message.success('Applied template to all events');
        fetchEvents();
      } else {
        message.error('Failed to apply template to all events');
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to apply template to all events');
    }
  };

  /** "Create Events" from template action */
  const createAllEventsOnTemplate = async (name: string, start: string) => {
    try {
      const response = await api.post('/calendar/createAllEventsOnTemplate', {
        data: { templateGroup: name, start },
        getResponse: true,
      });
      if (response.status === 200) {
        message.success('Created all events on template');
        fetchEvents();
      } else {
        message.error('Failed to create all events on template');
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to create all events on template');
    }
  };

  /** Delete event action */
  const onDeleteEvent = async (eventId: string) => {
    try {
      const response  = await api.delete(`/calendar/${eventId}`);
      if (response.status === 200) {
        setEvents((prev) => prev.filter((e) => e.id !== eventId));
        message.success('Event deleted successfully');
      } else {
        message.error('Failed to delete event');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to delete event');
    }
  };

  /** Opens modal in edit mode */
  const onEditEvent = (evt: IEvent) => {
    setEditing(true);
    setEventFormVisible(true);
    setCurrentEvent(evt);
    setEventTitle(evt.title);
    setEventStart(evt.start ? dayjs(evt.start) : null);
    setEventEnd(evt.end ? dayjs(evt.end) : null);
    setEventLocation(evt.location || '');
    setEventDescription(evt.description || '');
    setEventAttendees(evt.eventAttendees || '');
    setTemplateMode(!!evt.templateMode);
    setTemplateName(evt.templateGroup || '');
    setAnchorEvent(evt.anchorEvent || '');
    setAnchorEventNext(evt.anchorEventNext || '');
    setOffsetType('after');
    setOffsetValue(evt.pivotEventOffset ? String(evt.pivotEventOffset) : '');
  };

  /** Creates a new event via POST */
  const handleCreateEvent = async () => {
    if (!eventTitle.trim()) {
      message.error('Event title cannot be empty!');
      return;
    }
    const payload: Partial<IEvent> = {
      title: eventTitle.trim(),
      start: eventStart ? eventStart.toISOString() : '',
      estateId: estateId,
      end: eventEnd ? eventEnd.toISOString() : '',
      location: eventLocation,
      description: eventDescription,
      eventAttendees,
      templateGroup: templateName,
      templateMode,
      pivotEventOffset: offsetValue,
      anchorEvent,
      anchorEventNext,
    };
    try {
      const response = await api.post('/calendar', payload);
      if (response.status === 200 || response.status === 201) {
        setEvents((prev) => [...prev, response.data]);
        message.success('Event created successfully');
        handleEventFormCancel();
      } else {
        message.error('Failed to create event');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to create event');
    }
  };

  /** Updates an existing event via PUT */
  const handleEditEvent = async () => {
    if (!eventTitle.trim() || !currentEvent) {
      message.error('Event title cannot be empty!');
      return;
    }
    const payload: Partial<IEvent> = {
      title: eventTitle.trim(),
      start: eventStart ? eventStart.toISOString() : '',
      end: eventEnd ? eventEnd.toISOString() : '',
      location: eventLocation,
      description: eventDescription,
      eventAttendees,
      templateMode,
      templateGroup: templateName,
      pivotEventOffset: offsetValue,
      anchorEvent,
      anchorEventNext,
    };
    try {
      const response = await api.put(`/calendar/${currentEvent.id}`, {
        data: payload,
        getResponse: true,
      });
      if (response.status === 200) {
        setEvents((prev) =>
          prev.map((item) =>
            item.id === currentEvent.id ? { ...item, ...response.data } : item
          )
        );
        message.success('Event updated successfully');
        handleEventFormCancel();
      } else {
        message.error('Failed to update event');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to update event');
    }
  };

  /** Resets modal fields and closes it */
  const handleEventFormCancel = () => {
    setEditing(false);
    setEventFormVisible(false);
    setCurrentEvent(null);
    setEventTitle('');
    setEventStart(null);
    setEventEnd(null);
    setEventLocation('');
    setEventDescription('');
    setEventAttendees('');
    setTemplateMode(false);
    setTemplateName('');
    setAnchorEvent('');
    setAnchorEventNext('');
    setOffsetType('after');
    setOffsetValue('');
  };

  /** For inline creation of new template items in the <Select> */
  const onNewTemplateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewTemplateName(e.target.value);
  };

  const addTemplateItem = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const timestamp = dayjs().valueOf();
    const freshTemplate: ITemplate = {
      id: String(timestamp),
      templateName: newTemplateName || `Calendar Template ${templates.length + 1}`,
    };
    setTemplates((prev) => [...prev, freshTemplate]);
    setNewTemplateName('');
    setTimeout(() => {
      calendarTemplateInputRef.current?.focus();
    }, 0);
  };

  return (
    <div style={{ padding: 24, background: '#fff' }}>
      <TemplateListTable
        templates={templates}
        onApplyToAll={onApplyToAll}
        createAllEventsOnTemplate={createAllEventsOnTemplate}
        isAdmin={isAdmin}
      />

      <Divider />

      <Calendar
        dateCellRender={handleDateCellRender}
        onSelect={handleDateSelect}
      />

      <Modal
        title={editing ? 'Edit Event' : 'Create Event'}
        open={eventFormVisible}
        onOk={editing ? handleEditEvent : handleCreateEvent}
        onCancel={handleEventFormCancel}
      >
        {/* If in template mode, show pivot/offset fields */}
        {templateMode && (
          <>
            <Divider />
            <div style={{ marginBottom: 8 }}>Preceding Pivot Event</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a Previous event"
              value={anchorEvent}
              onChange={(value) => setAnchorEvent(value)}
              style={{ width: '100%', marginBottom: 16 }}
            >
              {events.map((ev) => (
                <Option key={ev.id} value={ev.id}>
                  {ev.title}
                </Option>
              ))}
            </Select>

            <div style={{ marginBottom: 8 }}>Next Pivot Event</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a Next event"
              value={anchorEventNext}
              onChange={(value) => setAnchorEventNext(value)}
              style={{ width: '100%', marginBottom: 16 }}
            >
              {events.map((ev) => (
                <Option key={ev.id} value={ev.id}>
                  {ev.title}
                </Option>
              ))}
            </Select>

            <div style={{ marginBottom: 8 }}>Select a template</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a Template or Create a new one"
              value={templateName}
              onChange={(value) => setTemplateName(value)}
              style={{ width: '100%', marginBottom: 16 }}
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Input
                      placeholder="Please enter item"
                      ref={calendarTemplateInputRef}
                      value={newTemplateName}
                      onChange={onNewTemplateNameChange}
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={addTemplateItem}
                    >
                      Add Template item
                    </Button>
                  </Space>
                </div>
              )}
            >
              {templates.map((tpl) => (
                <Option key={tpl.id} value={tpl.templateName}>
                  {tpl.templateName}
                </Option>
              ))}
            </Select>

            <div style={{ marginBottom: 8 }}>Number of days offset from previous event</div>
            <div style={{ display: 'flex', marginBottom: 16 }}>
              <Select
                value={offsetType}
                onChange={(val: string) => setOffsetType(val)}
                style={{ width: '40%' }}
              >
                <Option value="after">Days after Previous Event</Option>
              </Select>
              <Input
                style={{ width: '60%' }}
                placeholder="Offset value"
                value={offsetValue}
                type="number"
                onChange={(e) => setOffsetValue(e.target.value)}
              />
            </div>
          </>
        )}

        <div style={{ marginBottom: 8 }}>Event Title</div>
        <Input
          placeholder="Event title"
          value={eventTitle}
          onChange={(e) => setEventTitle(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        {/* Only show date pickers if NOT a template event */}
        {!templateMode && (
          <>
            <div style={{ marginBottom: 8 }}>Event Start Date</div>
            <DatePicker
              placeholder="Start date"
              value={eventStart}
              onChange={(date) => setEventStart(date)}
              style={{ marginBottom: 16, width: '100%' }}
            />

            <div style={{ marginBottom: 8 }}>Event End Date</div>
            <DatePicker
              placeholder="End date"
              value={eventEnd}
              onChange={(date) => setEventEnd(date)}
              style={{ marginBottom: 16, width: '100%' }}
            />
          </>
        )}

        <div style={{ marginBottom: 8 }}>Location of Event</div>
        <Input
          placeholder="Location"
          value={eventLocation}
          onChange={(e) => setEventLocation(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 8 }}>Description of Event</div>
        <Input
          placeholder="Description"
          value={eventDescription}
          onChange={(e) => setEventDescription(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 8 }}>Attendees of Event</div>
        <Input
          placeholder="Attendees"
          value={eventAttendees}
          onChange={(e) => setEventAttendees(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        <div style={{ marginBottom: 8 }}>Is this a Template Event?</div>
        <Switch checked={templateMode} onChange={(checked) => setTemplateMode(checked)} />
      </Modal>

      <Divider />

      <EventsTable events={events} onDelete={onDeleteEvent} onEdit={onEditEvent} />
    </div>
  );
};

export default CalendarComponent;
